import LazyImage from 'components/Image/LazyImage';
import noImage from 'components/Image/noIMG.svg';
import { AnalyzedItem } from 'containers/QuickOrder/types';
import { formatVND } from 'containers/QuickOrder/utils';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { ChangeEvent, Fragment, MouseEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styles/styled-components';
import translations from 'translations';
import { AiMatchedProduct, Product } from 'types/schema';
import { SearchIcon } from './icons';
import messages from './messages';
import { useProductSearch } from './useProductSearch';

export function mapProductToAiMatched(p: Product): AiMatchedProduct {
  return {
    productId: p.id,
    sku: p.id,
    name: p.name ?? '',
    unit: (p.uom as unknown as string) ?? '',
    uom: (p.uom as unknown as string) ?? '',
    uomLocal: p.uomLocal ?? '',
    price: p.priceV2?.price ?? p.price,
    imageUrl: p.imageUrl ?? '',
    quantity: 1,
    netUom: '',
    baseUom: '',
    baseUnit: 0,
    netUnit: 0,
  };
}

interface Props {
  product: AnalyzedItem;
  anchorEl: HTMLElement | null;
  onSelect: (id: string, alternative: AiMatchedProduct) => void;
  onClose: () => void;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
`;

const Modal = styled.div<{ top: number; left: number }>`
  position: fixed;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: 384px;
  background: #ffffff;
  border: 1px solid #e5e8eb;
  border-radius: 16px;
  box-shadow: 0 4px 24px 0 rgba(58, 85, 9, 0.08);
  overflow: hidden;
  z-index: 1001;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px;
  padding: 4px 16px;
  border: 1.5px solid #19a54a;
  border-radius: 80px;
  background: #fff;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #111827;
  background: transparent;

  &::placeholder {
    color: #9ca3af;
  }
`;

const List = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const Separator = styled.div`
  height: 1px;
  background: #e5e8eb;
  margin: 0;
`;

const Suggest = styled.div`
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 4px 16px 2px;
`;

const ListWrap = styled.div<{ loading: boolean }>`
  position: relative;
  ${({ loading }) => (loading ? 'min-height: 120px;' : '')}
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 1;
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid #e5e8eb;
  border-top-color: #19a54a;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProductRow = styled.button<{ highlighted: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 16px;
  background: ${({ highlighted }) => (highlighted ? '#f5f5f5' : '#fff')};
  border: none;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f5f5f5;
  }
`;

const Thumb = styled(({ noImage: _noImage, ...rest }) => <LazyImage {...rest} />)<{ noImage: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  object-fit: ${({ noImage: noImg }) => (noImg ? 'contain' : 'cover')};
  ${({ noImage: noImg }) => (noImg ? 'opacity: 0.3;' : '')}
  flex-shrink: 0;
  background: #f5f5f5;
`;

const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductUnit = styled.div`
  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
  color: #6b7280;
`;

const ProductPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #111827;
  flex-shrink: 0;
`;

const ProductResultRow = ({ p, onClick }: { p: Product; onClick: () => void }) => {
  const { uomInfo } = useProductUomInfo(p);
  return (
    <ProductRow highlighted={false} onClick={onClick}>
      <Thumb noImage={!p.imageUrl} src={p.imageUrl || noImage} alt={p.name ?? ''} />
      <ProductInfo>
        <ProductName>{p.name}</ProductName>
        <ProductUnit>{uomInfo}</ProductUnit>
      </ProductInfo>
      <ProductPrice>{formatVND(p.priceV2?.price || 0)}</ProductPrice>
    </ProductRow>
  );
};

const AlternativeRow = ({ alt, onClick }: { alt: AiMatchedProduct; onClick: () => void }) => {
  const { uomInfo } = useProductUomInfo(alt);
  return (
    <ProductRow highlighted={false} onClick={onClick}>
      <Thumb noImage={!alt.imageUrl} src={alt.imageUrl || noImage} alt={alt.name} />
      <ProductInfo>
        <ProductName>{alt.name}</ProductName>
        <ProductUnit>{uomInfo}</ProductUnit>
      </ProductInfo>
      <ProductPrice>{formatVND(alt.price)}</ProductPrice>
    </ProductRow>
  );
};

const ChangeProductModal = ({ product, anchorEl, onSelect, onClose }: Props) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const getPos = useCallback(() => {
    const rect = anchorEl?.getBoundingClientRect();
    return { top: rect ? rect.bottom + 12 : 0, left: rect ? rect.left : 0 };
  }, [anchorEl]);

  const [pos, setPos] = useState(getPos);

  useEffect(() => {
    const update = () => setPos(getPos());
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [getPos]);

  const { results: searchResults, loading, searchProducts } = useProductSearch(product.matchedProduct?.productId);

  const handleQueryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setQuery(val);
      searchProducts(val);
    },
    [searchProducts],
  );

  const alternatives = product.alternatives ?? [];
  const trimmedQuery = query.trim();

  const handleOverlayClick = useCallback(
    (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    },
    [onClose],
  );

  const handleSelectAlternative = useCallback(
    (alt: AiMatchedProduct) => {
      onSelect(product.id, alt);
      onClose();
    },
    [product.id, onSelect, onClose],
  );

  const handleSelectProduct = useCallback(
    (p: Product) => {
      onSelect(product.id, mapProductToAiMatched(p));
      onClose();
    },
    [product.id, onSelect, onClose],
  );

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal ref={modalRef} top={pos.top} left={pos.left}>
        <SearchBar>
          <SearchIcon />
          <SearchInput
            ref={inputRef}
            autoFocus
            placeholder={translations(messages.searchPlaceholder)}
            value={query}
            onChange={handleQueryChange}
          />
        </SearchBar>
        <ListWrap loading={!!trimmedQuery && loading}>
          {trimmedQuery && loading && (
            <LoadingOverlay>
              <Spinner />
            </LoadingOverlay>
          )}
          <List>
            {!trimmedQuery && alternatives.length > 0 && <Suggest>{translations(messages.suggest)}</Suggest>}
            {trimmedQuery
              ? searchResults.map((p, idx) => (
                  <Fragment key={p.id}>
                    {idx > 0 && <Separator />}
                    <ProductResultRow p={p} onClick={() => handleSelectProduct(p)} />
                  </Fragment>
                ))
              : alternatives.map((alt, idx) => (
                  <Fragment key={alt.sku}>
                    {idx > 0 && <Separator />}
                    <AlternativeRow alt={alt} onClick={() => handleSelectAlternative(alt)} />
                  </Fragment>
                ))}
          </List>
        </ListWrap>
      </Modal>
    </Overlay>
  );
};

export default memo(ChangeProductModal);
